/**
 * Where a sheet's words come from, other than the box they can be typed in.
 *
 * A List 词单 publishes `/cidan/<path>/canonical.json`, which names its words
 * in study order, or names the parts it is split into. An HSK band is a List
 * like any other, split into parts of fifty. A signed-in reader's saved words
 * come from the same TSV export `/user/ci/` offers, which is free and is the
 * only route that answers with the whole list. It carries each word's tags,
 * which is how a sheet narrows the words to one. A reader's own Lists come
 * from `/user/cidan/words/`, which names them and gives one's words in the
 * order the reader set. See `OwnListWordsPage` in `src/user/`.
 *
 * Every source also names its 例句 by their ids, for the sentence sheet: a
 * List's object lists them among its members, an own List's answer apart
 * from its words, and `/user/liju/ids/` lists the reader's saved ones. See
 * `SavedLijuIdsPage`.
 */

/** One of the reader's own Lists, with its parts, as the worksheets offer it. */
export interface OwnListChoice {
  readonly id: string;
  readonly name: string;
  readonly words: number;
  readonly parts: readonly {
    readonly id: string;
    readonly name: string;
    readonly words: number;
  }[];
}

/** Where a reader's own Lists and their words are asked for. */
const ownListsAddress = "/user/cidan/words/";

/** Where a reader's saved 例句 are asked for. */
const savedSentencesAddress = "/user/liju/ids/";

/** A saved word and the reader's tags on it. */
export interface SavedWord {
  readonly word: string;
  readonly tags: readonly string[];

  /** The HSK band, as the export writes it, where the word list grades it. */
  readonly band?: string;
}

/** A List's words and 例句, and what it is called. */
export interface ListWords {
  readonly title: string;
  readonly hanzi: string;
  readonly words: readonly string[];

  /** Its 例句 by their ids, in study order. */
  readonly sentences: readonly string[];
}

/** What a sheet needs to read a List or a reader's words. */
export interface WorksheetSourcesProperties {
  readonly fetch: typeof fetch;
}

/** The address of a List's object, each segment of its path encoded. */
export function listAddress(path: string): string {
  const segments = path
    .split("/")
    .filter((segment) => segment !== "")
    .map((segment) => encodeURIComponent(segment));

  return `/cidan/${segments.join("/")}/canonical.json`;
}

/** A run of a List's parts that prints on one worksheet. */
interface ListSheet {
  readonly path: string;
  readonly title: string;
  readonly hanzi: string;
  readonly from: number;
  readonly to: number;
}

interface ListObject {
  readonly title: string;
  readonly hanzi: string;
  readonly children: readonly string[];
  readonly sheets: readonly ListSheet[];
  readonly words: readonly string[];
  readonly sentences: readonly string[];
}

function strings(value: unknown): readonly string[] {
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === "string")
    : [];
}

/** A List's object, read defensively, since it arrives over the network. */
function listObject(body: unknown): ListObject | undefined {
  if (typeof body !== "object" || body === null) {
    return undefined;
  }
  const fields = body as Record<string, unknown>;
  const members = Array.isArray(fields["members"]) ? fields["members"] : [];
  const named = (wanted: string, key: string): readonly string[] =>
    members.flatMap((member: unknown) => {
      if (typeof member !== "object" || member === null) {
        return [];
      }
      const fields = member as Record<string, unknown>;
      const value = fields[key];

      return fields["kind"] === wanted && typeof value === "string"
        ? [value]
        : [];
    });

  return {
    title: typeof fields["title"] === "string" ? fields["title"] : "",
    hanzi: typeof fields["hanzi"] === "string" ? fields["hanzi"] : "",
    children: strings(fields["children"]),
    sheets: Array.isArray(fields["sheets"])
      ? (fields["sheets"] as unknown[]).flatMap((value) => {
          const sheet = record(value);
          const from = sheet?.["from"];
          const to = sheet?.["to"];

          return typeof sheet?.["path"] === "string" &&
            typeof sheet["title"] === "string" &&
            typeof sheet["hanzi"] === "string" &&
            typeof from === "number" &&
            typeof to === "number"
            ? [
                {
                  path: sheet["path"],
                  title: sheet["title"],
                  hanzi: sheet["hanzi"],
                  from,
                  to,
                },
              ]
            : [];
        })
      : [],
    words: named("ci", "headword"),
    sentences: named("liju", "uuid"),
  };
}

/** An object's own keyed values, or nothing for anything else. */
function record(body: unknown): Readonly<Record<string, unknown>> | undefined {
  return typeof body === "object" && body !== null && !Array.isArray(body)
    ? (body as Record<string, unknown>)
    : undefined;
}

/** One own List as the route answers with it, read defensively. */
function ownListChoice(value: unknown): OwnListChoice | undefined {
  const list = record(value);
  if (typeof list?.["id"] !== "string" || typeof list["name"] !== "string") {
    return undefined;
  }
  const parts = Array.isArray(list["parts"]) ? list["parts"] : [];

  return {
    id: list["id"],
    name: list["name"],
    words: typeof list["words"] === "number" ? list["words"] : 0,
    parts: parts.flatMap((one: unknown) => {
      const part = record(one);

      return typeof part?.["id"] === "string" &&
        typeof part["name"] === "string"
        ? [
            {
              id: part["id"],
              name: part["name"],
              words: typeof part["words"] === "number" ? part["words"] : 0,
            },
          ]
        : [];
    }),
  };
}

/** How deep a List's parts are followed. An HSK band is one level deep. */
const maxDepth = 3;

/** The 汉字 column's header in the export. See `ciExportColumns`. */
const hanziColumn = "hanzi";

/** The tags column's header in the export. */
const tagsColumn = "tags";

/** The column of an exported TSV holding the HSK band, `HSK 2`. */
const bandColumn = "hsk";

/**
 * The tag the export puts last on every word, which is the site's and not
 * the reader's. See `siteTag` in `src/content/cidian/cidian-csv-columns.ts`.
 */
const siteTag = "chineseboost";

/** Reads a List's words or a reader's saved words for a sheet. */
export class WorksheetSources {
  readonly #fetch: typeof fetch;

  constructor(properties: WorksheetSourcesProperties) {
    this.#fetch = properties.fetch;
  }

  /**
   * A List's words, taking in every part of a List that is split into parts.
   *
   * A path ending in a run of parts, `hsk-7-9/1-4`, reads those parts of the
   * List, where the List names that run among its `sheets`. A band too long
   * for one worksheet prints a run at a time.
   * @param path - The List's path under `/cidan/`, such as `hsk-1`,
   * `weather/1` or `hsk-7-9/1-4`.
   * @returns The words in study order, or nothing for a List the site does
   * not publish.
   */
  async list(path: string): Promise<ListWords | undefined> {
    const run = /^(.+)\/\d+-\d+$/u.exec(path);
    const top = await this.#listObject(run?.[1] ?? path);
    if (top === undefined) {
      return undefined;
    }

    const sheet = top.sheets.find((one) => one.path === path);
    if (run !== null && sheet === undefined) {
      return undefined;
    }

    const parts = await this.#parts(
      sheet === undefined
        ? top.children
        : top.children.slice(sheet.from - 1, sheet.to),
      1,
    );

    if (sheet !== undefined) {
      return {
        title: sheet.title,
        hanzi: sheet.hanzi,
        words: parts.flatMap((part) => part.words),
        sentences: [...new Set(parts.flatMap((part) => part.sentences))],
      };
    }

    return {
      title: top.title,
      hanzi: top.hanzi,
      words: [...top.words, ...parts.flatMap((part) => part.words)],
      sentences: [
        ...new Set([
          ...top.sentences,
          ...parts.flatMap((part) => part.sentences),
        ]),
      ],
    };
  }

  /**
   * A signed-in reader's saved words with their tags, in the order the
   * export lists them.
   * @returns The words, or nothing when the export is not answered with a
   * list, which is what a reader with no session gets.
   */
  async saved(): Promise<readonly SavedWord[] | undefined> {
    const first = await this.#savedPart(1);
    if (first === undefined) {
      return undefined;
    }

    const rest = await Promise.all(
      Array.from({ length: first.parts - 1 }, (_, at) =>
        this.#savedPart(at + 2),
      ),
    );

    return [...first.words, ...rest.flatMap((part) => part?.words ?? [])];
  }

  /**
   * The reader's own Lists and their parts.
   * @returns The Lists, or nothing for a reader with no session or an answer
   * that is not them.
   */
  async ownLists(): Promise<readonly OwnListChoice[] | undefined> {
    const body = record(await this.#ownJson(ownListsAddress));
    const lists = body?.["lists"];

    return Array.isArray(lists)
      ? lists.flatMap((value: unknown) => {
          const choice = ownListChoice(value);

          return choice === undefined ? [] : [choice];
        })
      : undefined;
  }

  /**
   * One of the reader's own Lists, or a part of one, in the reader's order.
   * @param path - `<list>` or `<list>/<part>`, by id.
   * @returns Its name and words, or nothing for a List the reader does not
   * hold.
   */
  async ownList(path: string): Promise<ListWords | undefined> {
    const [list = "", part = ""] = path.split("/");
    const query = new URLSearchParams({ list });
    if (part !== "") {
      query.set("part", part);
    }
    const found = record(
      record(await this.#ownJson(`${ownListsAddress}?${query.toString()}`))?.[
        "list"
      ],
    );
    if (typeof found?.["name"] !== "string") {
      return undefined;
    }

    return {
      title: found["name"],
      hanzi: "",
      words: strings(found["words"]),
      sentences: strings(found["sentences"]),
    };
  }

  /**
   * A signed-in reader's saved 例句 by their ids, newest first.
   * @returns The ids, or nothing for a reader with no session.
   */
  async savedSentences(): Promise<readonly string[] | undefined> {
    const sentences = record(await this.#ownJson(savedSentencesAddress))?.[
      "sentences"
    ];

    return Array.isArray(sentences) ? strings(sentences) : undefined;
  }

  async #ownJson(address: string): Promise<unknown> {
    try {
      const response = await this.#fetch(address, {
        credentials: "same-origin",
      });

      return response.ok ? await response.json() : undefined;
    } catch {
      return undefined;
    }
  }

  /**
   * A List's parts, and their parts, in order.
   *
   * Each level is read at once, which is one round trip for an HSK band.
   */
  async #parts(
    paths: readonly string[],
    depth: number,
  ): Promise<readonly ListObject[]> {
    if (depth > maxDepth || paths.length === 0) {
      return [];
    }
    const parts = await Promise.all(
      paths.map((path) => this.#listObject(path)),
    );
    const nested = await Promise.all(
      parts.map(async (part) =>
        part === undefined
          ? []
          : [part, ...(await this.#parts(part.children, depth + 1))],
      ),
    );

    return nested.flat();
  }

  async #listObject(path: string): Promise<ListObject | undefined> {
    try {
      const response = await this.#fetch(listAddress(path));

      return response.ok ? listObject(await response.json()) : undefined;
    } catch {
      return undefined;
    }
  }

  async #savedPart(
    part: number,
  ): Promise<{ words: readonly SavedWord[]; parts: number } | undefined> {
    try {
      const response = await this.#fetch(
        part <= 1 ? "/user/ci/tsv/" : `/user/ci/tsv/?part=${String(part)}`,
        { credentials: "same-origin" },
      );
      const type = response.headers.get("content-type") ?? "";
      if (!response.ok || !type.startsWith("text/tab-separated-values")) {
        return undefined;
      }

      return {
        words: tsvWords(await response.text()),
        parts: partsOf(response.headers.get("content-disposition") ?? ""),
      };
    } catch {
      return undefined;
    }
  }
}

/**
 * How many parts the export says there are.
 *
 * A list too long for one response is named `…-1-of-3.tsv`, and one that
 * fits is not numbered. See `ciDownloadFileName`.
 */
export function partsOf(disposition: string): number {
  const parts = /-\d+-of-(\d+)\.tsv/u.exec(disposition)?.[1];

  return parts === undefined ? 1 : Math.min(50, Number(parts));
}

/**
 * The words of an exported TSV, each with its tags.
 *
 * The file opens with the lines Anki reads before the first card, and the
 * `#columns:` line among them is its header. See `ankiPreamble` in
 * `src/user/ci-export.ts`. The export writes a tag's spaces as `_`, for
 * Anki, and they are put back here, so a tag reads as the reader wrote it.
 */
export function tsvWords(tsv: string): readonly SavedWord[] {
  const lines = tsv.split(/\r?\n/u);
  const header = (lines.find((line) => line.startsWith("#columns:")) ?? "")
    .slice("#columns:".length)
    .split("\t");
  const column = header.indexOf(hanziColumn);
  const tagsAt = header.indexOf(tagsColumn);
  const bandAt = header.indexOf(bandColumn);
  if (column < 0) {
    return [];
  }

  return lines
    .filter((line) => !line.startsWith("#"))
    .flatMap((row) => {
      const cells = row.split("\t");
      const word = cells[column]?.trim() ?? "";
      const written = (tagsAt < 0 ? "" : (cells[tagsAt] ?? ""))
        .split(" ")
        .filter((tag) => tag !== "");
      // The site's tag is the last one written, and only that one is the
      // site's. A reader may name a tag of their own the same.
      if (written.at(-1) === siteTag) {
        written.pop();
      }
      const tags = written.map((tag) => tag.replaceAll("_", " "));

      const band = bandAt < 0 ? "" : (cells[bandAt]?.trim() ?? "");

      return word === "" ? [] : [{ word, tags, ...(band !== "" && { band }) }];
    });
}

/** The 汉字 column of an exported TSV. */
export function tsvHanzi(tsv: string): readonly string[] {
  return tsvWords(tsv).map((saved) => saved.word);
}
